/**
 * Oeffentliche Routen der SiFa-Unterweisung (docs/sifa-ausbau2-plan.md §3.3)
 * — reines Parsen und Bauen der Hash-Adressen, ohne React.
 *
 *   #/unterweisung/<linkToken>[?von=<sammelToken>]   Teilnehmerseite
 *   #/unterweisung-test/<token>                      Teilnehmerseite im Testlauf
 *   #/unterweisungen/<sammelToken>                   Sammel-Link (Liste)
 *   #/datenschutz/unterweisung?l=…|?s=…|?t=…         Datenschutzhinweise
 *
 * Die Muster sind am Anfang UND hinter dem Segment verankert: „unterweisung"
 * ist Praefix von „unterweisungen" und „unterweisung-test" — ein lockeres
 * Muster wuerde die beiden anderen Seiten verschlucken.
 */

/** Woher die Datenschutzhinweise kommen: Einzel-, Sammel- oder Test-Link. */
export interface DatenschutzQuelle {
  art: 'link' | 'sammel' | 'test';
  token: string;
}

export type SifaRoute =
  | { seite: 'teilnahme'; linkToken: string; von: string | null }
  | { seite: 'test'; token: string }
  | { seite: 'sammel'; token: string }
  | { seite: 'datenschutz'; quelle: DatenschutzQuelle | null };

/** Zeichen eines Tokens (Hex, UUID, Base64url) — alles andere wird nie in einen Link uebernommen. */
const TOKEN = /^[A-Za-z0-9_-]+$/;

const gueltigerToken = (wert: string | null | undefined): string | null => {
  const t = (wert ?? '').trim();
  return t && TOKEN.test(t) ? t : null;
};

const abfrage = (roh: string | undefined): URLSearchParams => {
  try {
    return new URLSearchParams(roh ?? '');
  } catch {
    return new URLSearchParams();
  }
};

const SAMMEL = /^#\/unterweisungen\/([A-Za-z0-9_-]+)\/?(?:\?.*)?$/;
const TEST = /^#\/unterweisung-test\/([A-Za-z0-9_-]+)\/?(?:\?.*)?$/;
const TEILNAHME = /^#\/unterweisung\/([A-Za-z0-9_-]+)\/?(?:\?(.*))?$/;
const DATENSCHUTZ = /^#\/datenschutz\/unterweisung\/?(?:\?(.*))?$/;

/** Kuerzel der Abfrage je Quelle — l = Einzel-Link, s = Sammel-Link, t = Test-Link. */
const KUERZEL: Record<DatenschutzQuelle['art'], string> = { link: 'l', sammel: 's', test: 't' };

/**
 * Ordnet einen Hash einer SiFa-Seite zu; null = keine SiFa-Route (dann
 * entscheidet index.tsx weiter). Ein `von` mit fremden Zeichen faellt weg —
 * die Seite laeuft dann ohne Ruecklink, statt einen kaputten Link zu bauen.
 */
export const parseSifaRoute = (hash: string | null | undefined): SifaRoute | null => {
  const h = hash ?? '';
  let m = SAMMEL.exec(h);
  if (m) return { seite: 'sammel', token: m[1] };
  m = TEST.exec(h);
  if (m) return { seite: 'test', token: m[1] };
  m = TEILNAHME.exec(h);
  if (m) return { seite: 'teilnahme', linkToken: m[1], von: gueltigerToken(abfrage(m[2]).get('von')) };
  m = DATENSCHUTZ.exec(h);
  if (m) {
    const q = abfrage(m[1]);
    // Es steht immer nur eines da; falls doch mehrere, gewinnt die feste Reihenfolge.
    for (const art of ['link', 'sammel', 'test'] as const) {
      const token = gueltigerToken(q.get(KUERZEL[art]));
      if (token) return { seite: 'datenschutz', quelle: { art, token } };
    }
    return { seite: 'datenschutz', quelle: null };
  }
  return null;
};

const enc = (s: string): string => encodeURIComponent((s ?? '').trim());

/** `#/unterweisung/<linkToken>`, mit Sammel-Link als Herkunft `?von=<token>`. */
export const teilnahmeHref = (linkToken: string, von?: string | null): string => {
  const v = gueltigerToken(von);
  return `#/unterweisung/${enc(linkToken)}${v ? `?von=${enc(v)}` : ''}`;
};

/** `#/unterweisungen/<sammelToken>` — die Liste, Ziel des Ruecklinks. */
export const sammelHref = (token: string): string => `#/unterweisungen/${enc(token)}`;

/** `#/datenschutz/unterweisung?l=…` (bzw. `s`, `t`). */
export const datenschutzHref = (quelle: DatenschutzQuelle): string =>
  `#/datenschutz/unterweisung?${KUERZEL[quelle.art]}=${enc(quelle.token)}`;

/** Der Pfad eines Hashs ohne Abfrage und ohne Schraegstrich am Ende. */
export const routenPfad = (hash: string | null | undefined): string =>
  (hash ?? '').split('?')[0].replace(/\/+$/, '');

/**
 * Woran index.tsx erkennt, ob ein Hash-Wechsel eine andere Seite meint (dann
 * Neuladen). In der Regel der Pfad: ein `?von=` aendert die Seite nicht. Bei
 * den Datenschutzhinweisen steckt die Quelle (l/s/t) aber IN der Abfrage —
 * dort zaehlt der ganze Hash, sonst bliebe nach einem Quellwechsel der Text
 * der alten Firma stehen.
 */
export const routenSchluessel = (hash: string | null | undefined): string => {
  const pfad = routenPfad(hash);
  return pfad === '#/datenschutz/unterweisung' ? (hash ?? '').trim() : pfad;
};
